import { branch } from '@ipfabric/utils/fp/function';
import { addPropMutable } from '@ipfabric/utils/fp/prop';
import { pipe } from 'fp-ts/lib/function';
import type { DisplayObject } from 'pixi.js';

import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';

export interface CompositeHiding<TReason> {
  readonly visible: boolean;
  readonly reasonsToHide: Set<TReason>;
}

/**
 * Show element only when none of possible reasons to hide it is present
 *
 * Sometimes there are multiple reasons to hide an element (out of viewport,
 * hidden by filters or other temporary condition). Even if some condition
 * ceases (element gets back to viewport), others may prevail (still hidden by filters).
 *
 * This utility enriches PIXI DisplayObjects with a set of reasons to hide, making
 * the element visible only when the set is empty.
 *
 * You can pass any kind of value to `.reasonsToHide`, but symbols or objects are recommended
 * for their unique identity.
 */
export function installCompositeHiding<
  TPixiObject extends DisplayObject,
  TReason = unknown,
>(
  displayObject: TPixiObject,
  isVisible: (reasonsToHide: Set<TReason>) => boolean = (reasonsToHide) =>
    reasonsToHide.size === 0,
  onChange?: () => void,
): TPixiObject & CompositeHiding<TReason> {
  if (
    process.env.NODE_ENV === 'development' &&
    isFeatureFlagEnabled('ENABLE_GRAPH_DEBUG_HIDDEN_NODE_REASONS')
  ) {
    return installCompositeHidingDebug(displayObject);
  }

  const reasonsToHide = new Set<TReason>();
  const enrichedDisplayObject = addPropMutable(
    'reasonsToHide',
    reasonsToHide,
  )(displayObject);
  if (onChange) {
    const originalAdd = reasonsToHide.add;
    reasonsToHide.add = (...args) =>
      pipe(
        originalAdd.call(reasonsToHide, ...args),
        branch(() => onChange()),
      );
    const originalDelete = reasonsToHide.delete;
    reasonsToHide.delete = (...args) =>
      pipe(
        originalDelete.call(reasonsToHide, ...args),
        branch(() => onChange()),
      );
  }

  Object.defineProperty(displayObject, 'visible', {
    get() {
      return isVisible(this.reasonsToHide);
    },
    set() {
      throw new Error(
        'Visibility is not writable for objects enhanced with makeCompositeHiding',
      );
    },
  });
  return enrichedDisplayObject;
}

/**
 * Show reasons why a node was hidden
 *
 * Using this, hidden nodes are rendered as semi-transparent and
 * when you click on such node, a set of reasons why it was hidden
 * is printed to console.
 */
function installCompositeHidingDebug<
  TPixiObject extends DisplayObject,
  TReason = unknown,
>(displayObject: TPixiObject): TPixiObject & CompositeHiding<TReason> {
  const reasons: Set<TReason> & {
    addOriginal: Set<TReason>['add'];
    deleteOriginal: Set<TReason>['delete'];
  } = new Set<unknown>() as any;
  reasons.addOriginal = reasons.add;
  reasons.deleteOriginal = reasons.delete;

  const originalAlpha = displayObject.alpha;
  reasons.add = function add(value: TReason) {
    reasons.addOriginal(value);
    displayObject.alpha = originalAlpha / 6;
    return this;
  };
  reasons.delete = function del(value: TReason) {
    const result = reasons.deleteOriginal(value);
    if (this.size === 0) {
      displayObject.alpha = originalAlpha;
    }
    return result;
  };

  const enrichedDisplayObject = addPropMutable(
    'reasonsToHide',
    reasons,
  )(displayObject);
  enrichedDisplayObject.on('mousedown', () =>
    console.log(Array.from((displayObject as any).reasonsToHide)),
  );
  Object.defineProperty(enrichedDisplayObject, 'visible', {
    get() {
      return true;
    },
    set() {
      throw new Error(
        'Visibility is not writable for objects enhanced with makeCompositeHiding',
      );
    },
  });
  return enrichedDisplayObject;
}
